<template>
  <div class="profile-page">
    <div class="profile-card">
      <header class="profile-header">
        <div class="profile-icon" aria-hidden="true">
          <i class="fas fa-user"></i>
        </div>
        <h1 class="profile-title">Личный кабинет</h1>
        <p class="profile-subtitle">Данные регистрации можно изменить в любой момент</p>
      </header>

      <section class="profile-meta" aria-label="Параметры учётной записи">
        <div class="profile-meta__item">
          <span class="profile-meta__label">Имя для входа</span>
          <span class="profile-meta__value">{{ user?.username || '—' }}</span>
        </div>
        <div class="profile-meta__item">
          <span class="profile-meta__label">Роль</span>
          <span class="profile-meta__value">{{ roleLabel }}</span>
        </div>
        <div class="profile-meta__item">
          <span class="profile-meta__label">Квота расчётов</span>
          <span class="profile-meta__value">{{ quotaLabel }}</span>
        </div>
      </section>

      <form class="profile-form" @submit.prevent="saveProfile">
        <h2 class="profile-section-title">Личные данные</h2>

        <div class="profile-field">
          <label for="profile-user-type" class="form-label form-label-modern">Тип пользователя</label>
          <select
            id="profile-user-type"
            v-model="form.userType"
            class="form-select form-select-modern"
            required
          >
            <option value="individual">Частное лицо</option>
            <option value="organization">Организация</option>
          </select>
        </div>

        <div class="profile-grid">
          <div class="profile-field">
            <label for="profile-last-name" class="form-label form-label-modern">Фамилия</label>
            <input
              id="profile-last-name"
              v-model="form.lastName"
              type="text"
              class="form-control form-control-modern"
              required
              autocomplete="family-name"
              placeholder="Иванов"
            >
          </div>
          <div class="profile-field">
            <label for="profile-first-name" class="form-label form-label-modern">Имя</label>
            <input
              id="profile-first-name"
              v-model="form.firstName"
              type="text"
              class="form-control form-control-modern"
              required
              autocomplete="given-name"
              placeholder="Иван"
            >
          </div>
          <div class="profile-field">
            <label for="profile-patronymic" class="form-label form-label-modern">Отчество</label>
            <input
              id="profile-patronymic"
              v-model="form.patronymic"
              type="text"
              class="form-control form-control-modern"
              autocomplete="additional-name"
              placeholder="Иванович"
            >
          </div>
        </div>

        <div class="profile-field">
          <label for="profile-email" class="form-label form-label-modern">Электронная почта</label>
          <input
            id="profile-email"
            v-model="form.email"
            type="email"
            class="form-control form-control-modern"
            required
            autocomplete="email"
            placeholder="name@example.com"
          >
        </div>

        <div class="profile-field">
          <label for="profile-position" class="form-label form-label-modern">Должность</label>
          <input
            id="profile-position"
            v-model="form.position"
            type="text"
            class="form-control form-control-modern"
            required
            autocomplete="organization-title"
            placeholder="Врач-радиолог"
          >
        </div>

        <div v-if="form.userType === 'individual'" class="profile-field">
          <label for="profile-workplace" class="form-label form-label-modern">Место работы</label>
          <input
            id="profile-workplace"
            v-model="form.workplace"
            type="text"
            class="form-control form-control-modern"
            required
            autocomplete="organization"
            placeholder="Клиника, НИИ, больница"
          >
        </div>

        <template v-if="form.userType === 'organization'">
          <div class="profile-field">
            <label for="profile-organization" class="form-label form-label-modern">Название организации</label>
            <input
              id="profile-organization"
              v-model="form.organizationName"
              type="text"
              class="form-control form-control-modern"
              required
              placeholder="Название организации"
            >
          </div>
          <div class="profile-field">
            <label for="profile-contact" class="form-label form-label-modern">Контактное лицо</label>
            <input
              id="profile-contact"
              v-model="form.contactPerson"
              type="text"
              class="form-control form-control-modern"
              required
              placeholder="Имя контактного лица"
            >
          </div>
        </template>

        <button type="submit" class="btn btn-primary-modern profile-submit" :disabled="isSavingProfile">
          <span v-if="!isSavingProfile">
            <i class="fas fa-save me-2" aria-hidden="true"></i>
            Сохранить данные
          </span>
          <span v-else>
            <span class="spinner-border spinner-border-sm spinner-border-brand me-2" role="status"></span>
            Сохранение...
          </span>
        </button>
        <p v-if="profileError" class="profile-message profile-message--error">{{ profileError }}</p>
        <p v-if="profileSuccess" class="profile-message profile-message--success">{{ profileSuccess }}</p>
      </form>

      <form class="profile-form profile-form--password" @submit.prevent="changePassword">
        <h2 class="profile-section-title">Смена пароля</h2>
        <div class="profile-field">
          <label for="oldPassword" class="form-label form-label-modern">Текущий пароль</label>
          <input
            id="oldPassword"
            v-model="oldPassword"
            type="password"
            class="form-control form-control-modern"
            required
            autocomplete="current-password"
          >
        </div>
        <div class="profile-field">
          <label for="newPassword" class="form-label form-label-modern">Новый пароль</label>
          <input
            id="newPassword"
            v-model="newPassword"
            type="password"
            class="form-control form-control-modern"
            required
            autocomplete="new-password"
          >
        </div>
        <div class="profile-field">
          <label for="newPassword2" class="form-label form-label-modern">Подтвердите новый пароль</label>
          <input
            id="newPassword2"
            v-model="newPassword2"
            type="password"
            class="form-control form-control-modern"
            required
            autocomplete="new-password"
          >
        </div>
        <button type="submit" class="btn btn-primary-modern profile-submit" :disabled="isChangingPassword">
          Сменить пароль
        </button>
        <p v-if="passwordError" class="profile-message profile-message--error">{{ passwordError }}</p>
        <p v-if="passwordSuccess" class="profile-message profile-message--success">{{ passwordSuccess }}</p>
      </form>
    </div>

    <LoadingOverlay :is-loading="isSavingProfile || isChangingPassword" />
  </div>
</template>

<script>
import { useUserStore } from '@/stores/userStore';
import { apiErrorMessage } from '../api/errors';
import { PROFILE_PASSWORD_CHANGE_FAILED, PROFILE_UPDATE_FAILED } from '@/constants/userMessages';
import { changePassword, updateProfile } from '../api/modules/userApi';
import { ROLE_LABELS } from '@/constants/access';
import LoadingOverlay from '@/components/LoadingOverlay.vue';

function emptyForm() {
  return {
    userType: 'individual',
    lastName: '',
    firstName: '',
    patronymic: '',
    email: '',
    position: '',
    workplace: '',
    organizationName: '',
    contactPerson: '',
  };
}

export default {
  name: 'UserProfile',
  components: { LoadingOverlay },
  data() {
    return {
      form: emptyForm(),
      isSavingProfile: false,
      isChangingPassword: false,
      oldPassword: '',
      newPassword: '',
      newPassword2: '',
      profileError: null,
      profileSuccess: null,
      passwordError: null,
      passwordSuccess: null,
    };
  },
  setup() {
    const userStore = useUserStore();
    return { userStore };
  },
  computed: {
    user() {
      return this.userStore.user;
    },
    roleLabel() {
      return ROLE_LABELS[Number(this.user?.role)] || '—';
    },
    quotaLabel() {
      const quota = this.user?.quota;
      if (!quota) return '—';
      if (quota.limit == null) {
        return `без лимита, использовано ${quota.used || 0} в ${quota.period || 'этом месяце'}`;
      }
      const remaining = quota.remaining == null
        ? Math.max((quota.limit || 0) - (quota.used || 0), 0)
        : quota.remaining;
      return `осталось ${remaining} из ${quota.limit} в ${quota.period || 'этом месяце'}`;
    },
  },
  methods: {
    fillFromUser() {
      const user = this.user || {};
      this.form = {
        userType: user.user_type === 'organization' ? 'organization' : 'individual',
        lastName: user.last_name || '',
        firstName: user.first_name || '',
        patronymic: user.patronymic || '',
        email: user.email || '',
        position: user.position || '',
        workplace: user.workplace || '',
        organizationName: user.organization_name || '',
        contactPerson: user.contact_person || '',
      };
    },
    async saveProfile() {
      this.isSavingProfile = true;
      this.profileError = null;
      this.profileSuccess = null;
      const payload = {
        user_type: this.form.userType,
        last_name: this.form.lastName.trim(),
        first_name: this.form.firstName.trim(),
        patronymic: this.form.patronymic.trim(),
        email: this.form.email.trim(),
        position: this.form.position.trim(),
      };
      if (this.form.userType === 'organization') {
        payload.organization_name = this.form.organizationName.trim();
        payload.contact_person = this.form.contactPerson.trim();
      } else {
        payload.workplace = this.form.workplace.trim();
      }
      try {
        const user = await updateProfile(payload);
        this.userStore.setUser({ ...this.user, ...user });
        this.fillFromUser();
        this.profileSuccess = 'Личные данные сохранены.';
      } catch (error) {
        this.profileError = apiErrorMessage(error, PROFILE_UPDATE_FAILED);
      } finally {
        this.isSavingProfile = false;
      }
    },
    async changePassword() {
      if (this.newPassword !== this.newPassword2) {
        this.passwordError = 'Новые пароли не совпадают!';
        this.passwordSuccess = null;
        return;
      }
      this.isChangingPassword = true;
      this.passwordError = null;
      this.passwordSuccess = null;
      try {
        await changePassword({
          old_password: this.oldPassword,
          new_password: this.newPassword,
          new_password2: this.newPassword2,
        });
        this.passwordSuccess = 'Пароль успешно изменен!';
        this.oldPassword = '';
        this.newPassword = '';
        this.newPassword2 = '';
      } catch (error) {
        this.passwordError = apiErrorMessage(error, PROFILE_PASSWORD_CHANGE_FAILED);
      } finally {
        this.isChangingPassword = false;
      }
    },
  },
  async mounted() {
    await this.userStore.refreshUser();
    this.fillFromUser();
  },
};
</script>

<style scoped>
.profile-page {
  min-height: calc(100vh - 80px);
  display: flex;
  justify-content: center;
  padding: 2rem 1rem 3rem;
  background: var(--tcp-gradient-hero-bg);
}

.profile-card {
  background: var(--tcp-color-surface);
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  padding: 2.5rem;
  width: 100%;
  max-width: 760px;
  animation: profileSlideUp 0.5s ease-out;
}

.profile-header {
  text-align: center;
  margin-bottom: 1.75rem;
}

.profile-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 1rem;
  background: var(--tcp-gradient-brand);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;
  box-shadow: var(--tcp-shadow-brand-lg);
}

.profile-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--tcp-color-text-heading);
  margin: 0 0 0.35rem;
}

.profile-subtitle {
  margin: 0;
  color: var(--tcp-color-text-slate);
}

.profile-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 2rem;
}

.profile-meta__item {
  background: var(--tcp-color-surface-panel);
  border: 1px solid var(--tcp-color-border-subtle);
  border-radius: var(--tcp-radius-lg);
  padding: 0.85rem 1rem;
}

.profile-meta__label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--tcp-color-text-slate);
  margin-bottom: 0.25rem;
}

.profile-meta__value {
  display: block;
  color: var(--tcp-color-text-heading);
  font-weight: 600;
  word-break: break-word;
}

.profile-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.profile-form--password {
  margin-top: 2rem;
  padding-top: 1.75rem;
  border-top: 2px solid var(--tcp-color-border-subtle);
}

.profile-section-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--tcp-color-text-heading);
}

.profile-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.profile-field {
  min-width: 0;
}

.profile-submit {
  align-self: flex-start;
  margin-top: 0.25rem;
}

.profile-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.profile-message {
  margin: 0;
  font-weight: 600;
}

.profile-message--error {
  color: var(--tcp-color-danger);
}

.profile-message--success {
  color: var(--tcp-color-success-start);
}

@keyframes profileSlideUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 720px) {
  .profile-card {
    padding: 1.5rem 1.1rem;
  }

  .profile-title {
    font-size: 1.6rem;
  }

  .profile-meta,
  .profile-grid {
    grid-template-columns: 1fr;
  }
}
</style>
